'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { FormattedMessage } from '@flipster/i18n';
import { Styles, css } from '@flipster/styled-system/css';
import { center, grid, square, stack } from '@flipster/styled-system/patterns';

import { GlassCard } from '../../shared/glass-card';
import { vipProgramPageBunja } from '../../state';
import DiscountIcon from './assets/1-discount.svg';
import GiftIcon from './assets/2-gift.svg';
import ManagerIcon from './assets/3-manager.svg';
import SupportIcon from './assets/4-support.svg';

interface Props {
  css?: Styles;
}

export const PrivilegesSection = ({ css: cssProps }: Props) => {
  const { maxMakerDiscountAtom, maxTakerDiscountAtom } = useBunja(vipProgramPageBunja);
  const maxMakerDiscount = useAtomValue(maxMakerDiscountAtom);
  const maxTakerDiscount = useAtomValue(maxTakerDiscountAtom);

  if (!maxMakerDiscount || !maxTakerDiscount) return null;

  return (
    <section
      className={css(
        center.raw({
          flexDir: 'column',
          py: { base: 40, tablet: 64 },
          gap: 40,
          px: 16,
        }),
        cssProps,
      )}
    >
      <h2
        className={css({
          textStyle: 'Display/XXL',
          textAlign: 'center',
          background: 'linear-gradient(90deg, #FFD084 0%, #FFFCEE 100%)',
          backgroundClip: 'text',
          color: 'transparent',
        })}
      >
        <FormattedMessage id='Vip.r260121.VIP_exclusive_privileges' />
      </h2>
      <div
        className={grid({
          gap: { base: 12, tablet: 16 },
          gridTemplateColumns: { base: '1fr', tablet: 'repeat(4, 1fr)' },
          w: '100%',
        })}
      >
        <PrivilegeCard
          icon={<DiscountIcon />}
          title={<FormattedMessage id='Vip.r260121.Discounted_trading_fees' />}
          description={
            <FormattedMessage
              id='Vip.r260309.Up_to_percent_off_maker_fees_and_percent_off_taker_fees'
              values={{
                percent1: `${maxMakerDiscount}%`,
                percent2: `${maxTakerDiscount}%`,
              }}
            />
          }
        />
        <PrivilegeCard
          icon={<GiftIcon />}
          title={<FormattedMessage id='Vip.r260121.Customized_gifts' />}
          description={<FormattedMessage id='Vip.r260121.Exclusive_gifts_tailored_for_VIP_members' />}
        />
        <PrivilegeCard
          icon={<ManagerIcon />}
          title={<FormattedMessage id='Vip.r260121.Dedicated_manager' />}
          description={<FormattedMessage id='Vip.r260121.one_1_support_from_a_dedicated_VIP_relationship_manager' />}
        />
        <PrivilegeCard
          icon={<SupportIcon />}
          title={<FormattedMessage id='Vip.r260121.Priority_Support' />}
          description={
            <FormattedMessage id='Vip.r260121.Faster_support_and_access_to_VIP_only_campaigns_and_rewards' />
          }
        />
      </div>
    </section>
  );
};

interface PrivilegeCardProps {
  icon: React.ReactNode;
  title: React.ReactNode;
  description: React.ReactNode;
}

const PrivilegeCard = ({ icon, title, description }: PrivilegeCardProps) => (
  <GlassCard css={{ p: 24, gap: { base: 16, tablet: 24 } }}>
    <div
      className={square({
        size: 48,
        rounded: 12,
        bg: 'rgba(255, 208, 132, 0.14)',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'rgba(255, 208, 132, 0.25)',
      })}
    >
      {icon}
    </div>
    <div className={stack({ gap: 8 })}>
      <p className={css({ textStyle: 'Title/S', color: 'content.default.level-5' })}>{title}</p>
      <p className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-2' })}>{description}</p>
    </div>
  </GlassCard>
);
